/**
 * What the incoming ring sounds like, in one place.
 *
 * Three things need to agree on this: the oscillator ring a browser plays, the
 * audio file the desktop app plays, and the script that generates that file.
 * They used to be one, so the numbers lived inside it; now that there are two
 * players and a generator, a constant that drifts would mean the app rings at a
 * different pitch from the browser and nobody would notice for months.
 * `ringSpec.test.ts` reads the generated file back and checks it against these
 * numbers, so the drift is a failing test rather than a surprise.
 *
 * WHY THIS IS NOT THE RINGBACK TONE. It used to be: 425 Hz and 340 Hz, one
 * second on and four off, which is the Estonian tone a CALLER hears while
 * waiting. Playing the same thing at the person being called meant a call
 * coming in and a call going out were indistinguishable by ear, and the phone
 * on the desk is the one device where that distinction has to survive not
 * looking at the screen. So an incoming call now trills - two tones swapping
 * several times a second, twice in quick succession, then a pause. That is the
 * shape every desk phone has used for a century, and nothing else in the CRM
 * sounds remotely like it.
 */

/** The two tones the ring alternates between, a fifth apart. */
export const WARBLE_HZ = [660, 880] as const;

/** How long each tone holds before the ring swaps to the other one. */
export const WARBLE_SEC = 0.045;

/** A burst of ringing: where it starts within the cycle, and how long it lasts. */
export interface RingBurst {
  at: number;
  dur: number;
}

/**
 * Two short bursts rather than one long one.
 *
 * A single burst reads as an alert - a timer, an error, a notification. Two in
 * quick succession reads as a telephone, because that is what a telephone has
 * always done, and the pattern is recognised long before the pitch is.
 */
export const RING_BURSTS: readonly RingBurst[] = [
  { at: 0, dur: 0.4 },
  { at: 0.6, dur: 0.4 },
];

/** Ramp on each burst edge. Without it every burst starts with a click. */
export const RAMP_SEC = 0.02;

/** How long the ringing part lasts, silence between the bursts included. */
export const RING_LENGTH_SEC = RING_BURSTS.reduce((max, b) => Math.max(max, b.at + b.dur), 0);

/** Ring, pause, ring again. The pause is the gap between plays, not part of it. */
export const CYCLE_SEC = 3;

/** Quiet enough to sit under a conversation already happening in the room. */
export const GAIN = 0.06;

/**
 * How loud the file player should be for a given ring-volume setting.
 *
 * The oscillator ring reaches `GAIN` on a single warbling tone; the generated
 * file is normalised to full scale, so it needs the same figure to arrive at
 * the same loudness. Clamped because `HTMLMediaElement.volume` throws outside
 * 0..1, and the ring slider goes to 2.
 */
export function fileRingVolume(ringVolume: number): number {
  if (!Number.isFinite(ringVolume) || ringVolume <= 0) return 0;
  return Math.min(1, GAIN * ringVolume);
}

/** A ring that has started and can be stopped again. */
export interface Ring {
  stop: () => void;
}

/**
 * The frequency the warble is on at `offset` seconds into a burst.
 *
 * Pulled out of both players so the file and the oscillators step at the same
 * moments. Off-by-one here would be two rings a semitone apart on two machines.
 */
export function warbleAt(offset: number): number {
  const step = Math.floor(Math.max(0, offset) / WARBLE_SEC);
  return WARBLE_HZ[step % WARBLE_HZ.length] ?? WARBLE_HZ[0];
}

/** Envelope of one burst at `offset` seconds in, 0 at the edges and 1 inside. */
export function burstEnvelope(offset: number, dur: number): number {
  if (offset <= 0 || offset >= dur) return 0;
  if (offset < RAMP_SEC) return offset / RAMP_SEC;
  if (offset > dur - RAMP_SEC) return (dur - offset) / RAMP_SEC;
  return 1;
}
